import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { incomingInvoiceQueries } from '@/entities/incoming-invoice';

import { useNotificationsCtx } from '../../../model/context';

interface AutoDisplayProps {
  /**
   * Disable auto-open for urgent notifications
   * @default false
   */
  autoOpenDisabled?: boolean;
  /**
   * Base delay for auto-open
   * @default 500
   */
  autoOpenDelay?: number;
  /**
   * Disable auto-hide functionality
   * @default false
   */
  autoHideDisabled?: boolean;
  /**
   * Auto-hide delay in milliseconds
   * @default 60000 (1 minute)
   */
  autoHideDelay?: number;
}

const AutoDisplay = ({
  autoOpenDelay = 500,
  autoOpenDisabled = false,
  autoHideDisabled = false,
  autoHideDelay = 60_000
}: AutoDisplayProps) => {
  const queryClient = useQueryClient();

  const {
    drawer: { isOpen },
    urgentNotification,
    closeNotifications,
    openUrgentNotifications
  } = useNotificationsCtx();

  const autoHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAutoOpenedIdRef = useRef<string | null>(null);
  const seenWhileOpenRef = useRef<Set<string>>(new Set());
  const prevIsOpenRef = useRef(isOpen);
  const shouldAutoCloseRef = useRef(false);

  // Track notifications seen while drawer is open
  useEffect(() => {
    if (isOpen && urgentNotification) {
      seenWhileOpenRef.current.add(urgentNotification.id);
    }
  }, [isOpen, urgentNotification]);

  // Handle auto-opening of urgent notifications
  useEffect(() => {
    if (autoOpenDisabled || !urgentNotification || isOpen) {
      return;
    }

    const notAlreadyOpened = lastAutoOpenedIdRef.current !== urgentNotification.id;
    const notSeenWhileOpen = !seenWhileOpenRef.current.has(urgentNotification.id);

    if (notAlreadyOpened && notSeenWhileOpen) {
      const timeoutId = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: incomingInvoiceQueries.all() });
        openUrgentNotifications(urgentNotification);
        lastAutoOpenedIdRef.current = urgentNotification.id;
        shouldAutoCloseRef.current = true;
      }, autoOpenDelay);

      return () => clearTimeout(timeoutId);
    }
  }, [autoOpenDisabled, autoOpenDelay, isOpen, openUrgentNotifications, urgentNotification, queryClient]);

  // Track when urgent notifications should trigger auto-close
  useEffect(() => {
    if (urgentNotification && isOpen) {
      shouldAutoCloseRef.current = true;
    }
  }, [urgentNotification, isOpen]);

  // Handle auto-hiding after delay
  useEffect(() => {
    if (autoHideDisabled || !shouldAutoCloseRef.current || !isOpen) {
      return;
    }

    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
    }

    autoHideTimeoutRef.current = setTimeout(() => {
      closeNotifications();
      shouldAutoCloseRef.current = false;
    }, autoHideDelay);

    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }
    };
  }, [autoHideDisabled, autoHideDelay, isOpen, closeNotifications, shouldAutoCloseRef.current]);

  // Reset state when drawer closes
  useEffect(() => {
    const prevIsOpen = prevIsOpenRef.current;

    if (prevIsOpen && !isOpen) {
      seenWhileOpenRef.current.clear();
      shouldAutoCloseRef.current = false;
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  return null;
};

export default AutoDisplay;
